import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router';
import { useAuth } from '../auth/useAuth.js';
import { useFcmToken } from '../../hooks/useFcmToken.js';
import { apiClient } from '../../lib/api-client.js';

type NotifyField =
  | 'notifyCommentReply'
  | 'notifyRankingUpdate'
  | 'notifyBookmarkActivity'
  | 'notifySpotComment'
  | 'notifyDm';

function NotifyToggleRow({
  label,
  description,
  value,
  onToggle,
}: {
  label: string;
  description?: string;
  value: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex justify-between items-center px-4 py-3 gap-4">
      <div className="min-w-0">
        <p className="text-sm text-gray-700">{label}</p>
        {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
      </div>
      <button
        onClick={onToggle}
        className={`relative inline-flex w-10 h-6 items-center rounded-full transition-colors flex-shrink-0 ${
          value ? 'bg-primary' : 'bg-gray-300'
        }`}
        role="switch"
        aria-checked={value}
        aria-label={`${label}通知`}
      >
        <span
          className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${
            value ? 'translate-x-5' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

export function NotificationSettingsView() {
  const navigate = useNavigate();
  const { dbUser } = useAuth();
  const { permission: notifPermission, requestAndRegister } = useFcmToken();

  const [notifySpotComment, setNotifySpotComment] = useState<boolean>(
    dbUser?.notifySpotComment ?? true
  );
  const [notifyCommentReply, setNotifyCommentReply] = useState<boolean>(
    dbUser?.notifyCommentReply ?? true
  );
  const [notifyRankingUpdate, setNotifyRankingUpdate] = useState<boolean>(
    dbUser?.notifyRankingUpdate ?? true
  );
  const [notifyBookmarkActivity, setNotifyBookmarkActivity] = useState<boolean>(
    dbUser?.notifyBookmarkActivity ?? true
  );
  const [notifyDm, setNotifyDm] = useState<boolean>(
    (dbUser as (typeof dbUser & { notifyDm?: boolean }) | null)?.notifyDm ?? true
  );

  useEffect(() => {
    if (dbUser) {
      setNotifySpotComment(dbUser.notifySpotComment);
      setNotifyCommentReply(dbUser.notifyCommentReply);
      setNotifyRankingUpdate(dbUser.notifyRankingUpdate);
      setNotifyBookmarkActivity(dbUser.notifyBookmarkActivity);
      setNotifyDm((dbUser as typeof dbUser & { notifyDm?: boolean }).notifyDm ?? true);
    }
  }, [dbUser]);

  const notifySetters: Record<NotifyField, (v: boolean) => void> = {
    notifySpotComment: setNotifySpotComment,
    notifyCommentReply: setNotifyCommentReply,
    notifyRankingUpdate: setNotifyRankingUpdate,
    notifyBookmarkActivity: setNotifyBookmarkActivity,
    notifyDm: setNotifyDm,
  };

  const handleNotifyToggle = async (field: NotifyField, current: boolean) => {
    const newValue = !current;
    notifySetters[field](newValue);
    try {
      await apiClient.patch(
        '/users/me/notification-settings',
        { [field]: newValue },
        { requireAuth: true }
      );
    } catch {
      notifySetters[field](current);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* ヘッダー */}
      <div className="flex items-center gap-2 px-4 py-4 border-b border-gray-200">
        <button
          onClick={() => navigate(-1)}
          className="p-1 -ml-1 text-gray-500 hover:text-gray-700"
          aria-label="戻る"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className="text-lg font-bold text-gray-900">通知設定</h1>
      </div>

      <div className="p-4 space-y-6">
        {/* 通知未許可の場合 */}
        {typeof Notification !== 'undefined' && notifPermission !== 'granted' && (
          <div className="bg-white rounded-lg border border-gray-100 p-4 space-y-3">
            {notifPermission === 'denied' ? (
              <p className="text-sm text-gray-500 text-center">
                ブラウザの設定から通知を許可してください
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-700">
                  通知を有効にすると、コメントやランキング更新をプッシュ通知でお知らせします
                </p>
                <button
                  onClick={() => void requestAndRegister()}
                  className="w-full py-2 bg-primary text-white text-sm font-medium rounded-lg"
                >
                  通知を有効にする
                </button>
              </>
            )}
          </div>
        )}

        {/* トグル一覧（通知許可済みのみ） */}
        {notifPermission === 'granted' && (
          <div className="bg-white rounded-lg border border-gray-100 divide-y divide-gray-100">
            <NotifyToggleRow
              label="自分のスポットへのコメント"
              description="自分が登録したスポットに他のユーザーがコメントしたとき"
              value={notifySpotComment}
              onToggle={() => void handleNotifyToggle('notifySpotComment', notifySpotComment)}
            />
            <NotifyToggleRow
              label="コメント返信"
              description="自分のコメントに返信があったとき"
              value={notifyCommentReply}
              onToggle={() => void handleNotifyToggle('notifyCommentReply', notifyCommentReply)}
            />
            <NotifyToggleRow
              label="ランキング更新"
              description="週間・月間ランキングが更新されたとき"
              value={notifyRankingUpdate}
              onToggle={() => void handleNotifyToggle('notifyRankingUpdate', notifyRankingUpdate)}
            />
            <NotifyToggleRow
              label="ブックマーク新着"
              description="ブックマーク中のスポットに新しいコメント・写真が投稿されたとき"
              value={notifyBookmarkActivity}
              onToggle={() =>
                void handleNotifyToggle('notifyBookmarkActivity', notifyBookmarkActivity)
              }
            />
            <NotifyToggleRow
              label="ダイレクトメッセージ"
              description="他のユーザーからDMを受信したとき"
              value={notifyDm}
              onToggle={() => void handleNotifyToggle('notifyDm', notifyDm)}
            />
          </div>
        )}

        {/* プライバシー */}
        <div className="bg-white rounded-lg border border-gray-100 divide-y divide-gray-100">
          <Link
            to="/block-list"
            className="flex justify-between items-center px-4 py-3 text-sm text-gray-700"
          >
            ブロックリスト
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18l6-6-6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
}
